// Pure maths for the Radar widget (FE — #5 backlog), reused by the upcoming
// Passing widget. The widget is a thin SVG view; all the geometry + scaling lives
// here so it's unit-tested and pre-verifiable against real values.
//
// Scaling: each axis has fixed min/max BOUNDS (from radarTemplates.ts). A value
// maps to t = (v - min) / (max - min), clamped to [0,1] — 0 at the centre, 1 at
// the rim. min > max means an INVERTED axis (lower is better); the same formula
// handles it because the denominator goes negative. Percentage metrics are 0–1
// fractions in the data, so they're ×100 before scaling (bounds are in %).

import type { Bounds, RadarTemplate } from "./radarTemplates.js";

/** One resolved axis: the template metric + its display label + %-ness. */
export interface RadarMetric {
  key: string;
  label: string;
  bounds: Bounds;
  isPercentage: boolean;
}

/** Scale a raw value to 0..1 along its axis (null if we have no value). */
export function normalizeAxis(
  raw: number | string | null | undefined,
  bounds: Bounds,
  isPercentage: boolean,
): number | null {
  if (raw === null || raw === undefined) return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  const v = isPercentage ? n * 100 : n;
  const { min, max } = bounds;
  if (max === min) return 0;
  const t = (v - min) / (max - min);
  return Math.min(1, Math.max(0, t));
}

/** Angle (radians) of axis i of n, starting at the top and going clockwise. */
export function axisAngle(i: number, n: number): number {
  return -Math.PI / 2 + (i * 2 * Math.PI) / n;
}

export interface Point {
  x: number;
  y: number;
}

/** A point at distance r from (cx,cy) along `angle`. */
export function polar(cx: number, cy: number, r: number, angle: number): Point {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

export interface RadarAxis {
  key: string;
  label: string;
  /** The raw (already %-scaled if a percentage) numeric value, or null. */
  value: number | null;
  /** 0..1 position along the axis, or null when there's no value. */
  norm: number | null;
  angle: number;
  /** Where the polygon vertex sits (norm→center when null). */
  vertex: Point;
  /** Where to put the axis label (just outside the rim). */
  labelAt: Point;
  /** Text anchor for the label, from its angle (left/right/middle). */
  textAnchor: "start" | "middle" | "end";
}

export interface RadarShape {
  axes: RadarAxis[];
  /** "x,y x,y …" for the player's polygon (SVG points attr). */
  polygon: string;
}

/**
 * Build the full radar geometry for a template's metrics against a stats row.
 * `cx/cy/radius` are SVG units; `labelGap` pushes labels past the rim.
 */
export function buildRadar(
  metrics: RadarMetric[],
  row: Record<string, unknown> | null | undefined,
  cx: number,
  cy: number,
  radius: number,
  labelGap = 16,
): RadarShape {
  const n = metrics.length;
  const axes: RadarAxis[] = metrics.map((m, i) => {
    const raw = row ? (row[m.key] as number | string | null | undefined) : null;
    const norm = normalizeAxis(raw, m.bounds, m.isPercentage);
    const value =
      raw === null || raw === undefined || Number.isNaN(Number(raw))
        ? null
        : m.isPercentage
          ? Number(raw) * 100
          : Number(raw);
    const angle = axisAngle(i, n);
    const vertex = polar(cx, cy, radius * (norm ?? 0), angle);
    const labelAt = polar(cx, cy, radius + labelGap, angle);
    const cos = Math.cos(angle);
    const textAnchor: RadarAxis["textAnchor"] =
      cos > 0.3 ? "start" : cos < -0.3 ? "end" : "middle";
    return { key: m.key, label: m.label, value, norm, angle, vertex, labelAt, textAnchor };
  });
  const polygon = axes.map((a) => `${round(a.vertex.x)},${round(a.vertex.y)}`).join(" ");
  return { axes, polygon };
}

/**
 * Auto-pick the template for a player from their minutes-per-position. Takes the
 * position with the most minutes and returns the first template that covers it,
 * or null if none match (caller falls back to a default / the dropdown).
 */
export function pickTemplateByPosition(
  templates: RadarTemplate[],
  positionMinutes: { position_id: string; minutes: number }[],
): RadarTemplate | null {
  if (positionMinutes.length === 0) return null;
  const top = [...positionMinutes].sort((a, b) => b.minutes - a.minutes)[0]!;
  return templates.find((t) => t.positions.includes(top.position_id)) ?? null;
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
